import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  Calendar,
  MapPin,
  Award,
} from "lucide-react";

const InteractiveTimeline = () => {
  const [expandedItems, setExpandedItems] = useState(new Set([0])); // First item expanded by default

  const timelineData = [
    {
      id: 0,
      title: "Started Programming Journey",
      company: "Self-Taught",
      location: "Koforidua, Ghana",
      period: "2021",
      type: "milestone",
      description: "Built my first website and fell in love with programming.",
      achievements: [
        "Created first personal website using HTML and CSS",
        "Learned JavaScript fundamentals through online courses",
        "Joined local developer community and meetups",
        "Decided to pursue Computer Science in university",
      ],
      technologies: ["HTML", "CSS", "JavaScript"],
      color: "#06b6d4",
    },
    {
      id: 1,
      title: "Computer Science - Bachelor's Degree",
      company: "All Nations University",
      location: "Koforidua, Ghana",
      period: "Sep 2021 - Nov 15, 2025",
      type: "education",
      description:
        "Completed comprehensive Computer Science degree program and graduated on November 15, 2025. Served as President for two departments and contributed to establishing the ANU Football League, increasing its visibility and media presence while serving as platform moderator.",
      achievements: [
        "Graduated with B.Sc. in Computer Science (Nov 15, 2025)",
        "Served as President for two different departments at All Nations University",
        "Contributed to establishing and promoting the ANU Football League",
        "Served as platform moderator for the ANU Football League management system",
        "Successfully completed all required coursework and projects",
        "Built multiple real-world applications demonstrating technical proficiency",
      ],
      technologies: [
        "Computer Science",
        "Software Engineering",
        "Algorithms",
        "Data Structures",
        "Database Systems",
        "Web Development",
      ],
      color: "#8b5cf6",
    },
    {
      id: 2,
      title: "Software/AI Engineer",
      company: "Tboye Creative Solutions",
      location: "Accra, Ghana",
      period: "Jan 2022 - 2023",
      type: "work",
      description:
        "Developed and maintained multiple web applications using modern technologies.",
      achievements: [
        "Built 5 production web applications serving 10,000+ users",
        "Reduced bug reports by 50% through comprehensive testing",
        "Integrated payment systems increasing conversion by 25%",
        "Collaborated with design team to improve UX metrics by 35%",
      ],
      technologies: ["React", "Python", "Django", "PostgreSQL", "Redis"],
      color: "#06b6d4",
    },
    {
      id: 3,
      title: "Prompt Engineering & AI Integration",
      company: "Self-Learning / Professional Development",
      location: "Remote",
      period: "Early 2023",
      type: "learning",
      description:
        "Learned and mastered prompt engineering techniques for AI models, focusing on optimizing interactions with large language models like ChatGPT and Claude.",
      achievements: [
        "Mastered prompt engineering fundamentals and best practices",
        "Experimented with various AI models and tools (ChatGPT, Claude)",
        "Successfully integrated AI tools into daily development workflow",
        "Applied prompt engineering to solve real-world development challenges",
        "Improved development productivity through AI assistance",
      ],
      technologies: [
        "ChatGPT",
        "Claude AI",
        "Prompt Engineering",
        "AI-Assisted Development",
        "Large Language Models",
      ],
      color: "#a855f7",
    },
    {
      id: 4,
      title: "Graphic Design Journey",
      company: "Self-Directed Learning",
      location: "Accra, Ghana",
      period: "2023 - Present",
      type: "learning",
      description:
        "Started learning graphic design fundamentals, focusing on visual storytelling, branding, and digital content creation.",
      achievements: [
        "Designed social media graphics and branding assets with Canva",
        "Learned Photoshop techniques for photo editing and digital art",
        "Created poster and banner designs for personal and small projects",
        "Explored typography, color theory, and layout principles",
      ],
      technologies: ["Photoshop", "Canva", "Figma", "Illustrator (basic)"],
      color: "#10b981",
    },
    {
      id: 4.5,
      title: "Responsive Web Design",
      company: "freeCodeCamp",
      location: "Remote",
      period: "2023",
      type: "certification",
      description:
        "Earned freeCodeCamp's Responsive Web Design certification, covering HTML, CSS, Flexbox, CSS Grid, and responsive design principles.",
      achievements: [
        "Built responsive layouts using Flexbox and CSS Grid",
        "Mastered HTML5 semantic elements and accessibility best practices",
        "Completed 300+ hours of hands-on coding challenges and projects",
      ],
      technologies: ["HTML5", "CSS3", "Flexbox", "CSS Grid", "Responsive Design"],
      color: "#0a0a23",
      verificationUrl: "https://www.freecodecamp.org/certification/fcc270eedbd-aefc-458f-a008-d576814fab92/responsive-web-design",
    },
    {
      id: 5,
      title: "JavaScript (Intermediate) Certification",
      company: "HackerRank",
      location: "Remote",
      period: "Jan 2024",
      type: "certification",
      description:
        "Validated intermediate JavaScript programming skills through comprehensive testing.",
      achievements: [
        "Demonstrated proficiency in advanced JavaScript concepts",
        "Solved complex algorithmic challenges and problems",
        "Implemented efficient data structures and algorithms",
        "Achieved top percentile ranking among global developers",
      ],
      technologies: [
        "JavaScript",
        "Algorithms",
        "Data Structures",
        "Problem Solving",
      ],
      color: "#f59e0b",
    },
    {
      id: 6,
      title: "Content Creator & Video Editing Enthusiast",
      company: "Self-Driven Projects",
      location: "Accra, Ghana",
      period: "2024 - Present",
      type: "learning",
      description:
        "Exploring creative storytelling through video editing and content creation across digital platforms.",
      achievements: [
        "Created engaging video content for social media platforms",
        "Learned advanced editing techniques with industry-standard tools",
        "Built a growing online presence as a content creator",
        "Experimented with multimedia storytelling and audience engagement",
      ],
      technologies: ["Adobe Premiere Pro", "After Effects", "CapCut", "Figma"],
      color: "#3b82f6",
    },
    {
      id: 7,
      title: "Data Analytics Essentials",
      company: "Cisco Networking Academy",
      location: "Remote",
      period: "Oct 2024",
      type: "certification",
      description:
        "Comprehensive data analytics certification covering data collection, analysis, and visualization.",
      achievements: [
        "Mastered data collection and cleaning techniques",
        "Developed skills in data analysis and interpretation",
        "Created effective data visualizations for decision-making",
        "Applied statistical methods to real-world datasets",
      ],
      technologies: [
        "Data Analysis",
        "Statistics",
        "Data Visualization",
        "Python",
        "SQL",
      ],
      color: "#3b82f6",
    },
    {
      id: 8,
      title: "Digital Intelligence Job Simulation",
      company: "PwC Switzerland - Forage",
      location: "Remote",
      period: "Nov 2024",
      type: "certification",
      description:
        "Completed digital intelligence simulation focusing on business strategy and technology solutions.",
      achievements: [
        "Gained practical experience in digital intelligence methodologies",
        "Analyzed business requirements and proposed technology solutions",
        "Developed strategic recommendations for digital transformation",
        "Collaborated on virtual team projects simulating real-world scenarios",
      ],
      technologies: [
        "Digital Strategy",
        "Business Analysis",
        "Technology Solutions",
        "Data Interpretation",
      ],
      color: "#10b981",
    },
    {
      id: 9,
      title: "How to Edit Viral Videos",
      company: "Udemy",
      location: "Remote",
      period: "Oct 2024",
      type: "certification",
      description:
        "Completed a step-by-step course on editing viral video content, covering storytelling, pacing, hooks, and platform-optimized editing techniques.",
      achievements: [
        "Learned techniques for creating high-retention, viral-worthy video edits",
        "Applied editing principles using industry-standard tools",
        "Understood platform-specific formats for YouTube, TikTok, and Instagram Reels",
      ],
      technologies: ["Adobe Premiere Pro", "CapCut", "Video Editing", "Content Creation"],
      color: "#f59e0b",
      verificationUrl: "https://www.udemy.com/certificate/UC-1ba8b3c8-d3a9-47c3-9ca7-0b2325dd9b0c/",
    },
    {
      id: 10,
      title: "Social Media Management",
      company: "Great Learning",
      location: "Remote",
      period: "2024",
      type: "certification",
      description:
        "Certified in social media management covering content strategy, posting analytics, campaign management, and building brand presence across platforms.",
      achievements: [
        "Mastered social media management tools and scheduling platforms",
        "Learned to analyse content performance and optimize engagement",
        "Developed marketing strategies and social media campaigns",
        "Built skills in growing and maintaining brand presence online",
      ],
      technologies: [
        "Social Media Management",
        "Content Strategy",
        "Marketing Analytics",
        "Brand Building",
      ],
      color: "#3b82f6",
      verificationUrl: "https://www.mygreatlearning.com/certificate/PXLQBOUT",
    },
    {
      id: 11,
      title: "AI Fluency: Framework & Foundations",
      company: "Anthropic Education",
      location: "Remote",
      period: "Sep 5, 2026",
      type: "certification",
      description:
        "Completed Anthropic's AI Fluency course covering the foundational frameworks for understanding and working effectively with AI systems.",
      achievements: [
        "Gained deep understanding of AI frameworks and foundational concepts",
        "Learned how to evaluate and apply AI tools in real-world workflows",
        "Built fluency in working with large language models responsibly",
      ],
      technologies: ["AI", "Large Language Models", "Anthropic", "AI Ethics"],
      color: "#8b5cf6",
    },
  ];

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "work":
        return "💼";
      case "education":
        return "🎓";
      case "certification":
        return "🏆";
      case "milestone":
        return "🌟";
      default:
        return "📍";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "work":
        return "Work Experience";
      case "education":
        return "Education";
      case "certification":
        return "Certification";
      case "milestone":
        return "Milestone";
      default:
        return "Experience";
    }
  };

  return (
    <div className="relative">
      {/* Timeline Line */}
      <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 via-accent-500 to-primary-500" />

      {/* Timeline Items */}
      <div className="space-y-8">
        {timelineData.map((item, index) => {
          const isExpanded = expandedItems.has(item.id);

          return (
            <motion.div
              key={item.id}
              className="relative pl-20"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {/* Timeline Dot */}
              <motion.div
                className="absolute left-6 w-4 h-4 rounded-full border-4 border-white dark:border-dark-900 shadow-lg flex items-center justify-center"
                style={{ backgroundColor: item.color }}
                whileHover={{ scale: 1.2 }}
              >
                <div className="w-2 h-2 bg-white rounded-full" />
              </motion.div>

              {/* Content Card */}
              <motion.div
                className="bg-white dark:bg-dark-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden cursor-pointer"
                whileHover={{
                  scale: 1.02,
                  y: -2,
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                }}
                onClick={() => toggleExpanded(item.id)}
              >
                {/* Header */}
                <div className="p-6 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">
                          {getTypeIcon(item.type)}
                        </span>
                        <span
                          className="px-2 py-1 text-xs font-medium rounded-full text-white"
                          style={{ backgroundColor: item.color }}
                        >
                          {getTypeLabel(item.type)}
                        </span>
                      </div>

                      <h3 className="text-xl font-heading font-bold text-gray-900 dark:text-white mb-1">
                        {item.title}
                      </h3>

                      <p className="text-lg text-primary-600 dark:text-primary-400 font-medium mb-2">
                        {item.company}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <Calendar size={14} className="mr-1" />
                          {item.period}
                        </div>
                        <div className="flex items-center">
                          <MapPin size={14} className="mr-1" />
                          {item.location}
                        </div>
                      </div>
                    </div>

                    {/* Expand/Collapse Button */}
                    <motion.div
                      className="ml-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      animate={{ rotate: isExpanded ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronRight size={20} className="text-gray-400" />
                    </motion.div>
                  </div>

                  <p className="text-gray-600 dark:text-gray-400 mt-3 leading-relaxed">
                    {item.description}
                  </p>
                </div>

                {/* Expandable Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <div className="p-6">
                        {/* Key Achievements */}
                        <div className="mb-6">
                          <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-3">
                            <Award
                              size={18}
                              className="mr-2 text-primary-500"
                            />
                            Key Achievements
                          </h4>
                          <ul className="space-y-2">
                            {item.achievements.map(
                              (achievement, achievementIndex) => (
                                <motion.li
                                  key={achievementIndex}
                                  className="flex items-start text-gray-600 dark:text-gray-400"
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: achievementIndex * 0.1 }}
                                >
                                  <div
                                    className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0"
                                    style={{ backgroundColor: item.color }}
                                  />
                                  {achievement}
                                </motion.li>
                              )
                            )}
                          </ul>
                        </div>

                        {/* Technologies Used */}
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                            Technologies & Skills
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {item.technologies.map((tech, techIndex) => (
                              <motion.span
                                key={techIndex}
                                className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: techIndex * 0.05 }}
                                whileHover={{
                                  scale: 1.05,
                                  backgroundColor: item.color,
                                  color: "white",
                                }}
                              >
                                {tech}
                              </motion.span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default InteractiveTimeline;
